import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { BrandDispute, respondDispute } from '../../api/payment/brand-deal.api';
import { countdownLabel } from '../../utils/brand-creative';
import { BRAND_DISPUTE_LIMITS, disputeResultLines, disputeStatusLabel, validateResponseForm } from '../../utils/brand-dispute';

// Hộp kháng cáo (M4, Task 3) — dùng CHUNG cho creator (xem kháng cáo của mình) và doanh nghiệp/admin
// (xem + giải trình): trạng thái, mô tả + bằng chứng creator, giải trình của doanh nghiệp (kèm đồng hồ 3
// ngày + form giải trình nếu còn hạn và chưa gửi), kết quả + lý do khi admin đã quyết.

const errorMessage = (err: unknown, fallback: string) =>
  (err as { response?: { data?: { message?: string } } })?.response?.data?.message || fallback;

const STATUS_CLS: Record<string, string> = {
  Open: 'bg-amber-100 text-amber-700',
  Upheld: 'bg-rose-100 text-rose-700',
  Dismissed: 'bg-emerald-100 text-emerald-700',
};

export interface BrandDisputePanelProps {
  dealId: string;
  dispute: BrandDispute;
  /** "Creator" — bên kháng cáo, chỉ xem. "Business" — chủ brief, có thêm form giải trình. "Admin" — chỉ xem. */
  viewerRole: 'Creator' | 'Business' | 'Admin';
  nowMs: number;
  /** Gọi lại sau khi doanh nghiệp giải trình xong — cha cần cập nhật `dispute` mới nhất. */
  onResponded?: (dispute: BrandDispute) => void;
}

const BrandDisputePanel: React.FC<BrandDisputePanelProps> = ({ dealId, dispute, viewerRole, nowMs, onResponded }) => {
  const [response, setResponse] = useState('');
  const [links, setLinks] = useState<string[]>(['']);
  const [submitting, setSubmitting] = useState(false);

  const canRespond =
    viewerRole === 'Business' &&
    dispute.status === 'Open' &&
    !dispute.businessRespondedAt &&
    Date.parse(dispute.responseDueAt) > nowMs;

  const updateLink = (idx: number, value: string) => setLinks((prev) => prev.map((l, i) => (i === idx ? value : l)));
  const addLink = () => {
    if (links.length >= BRAND_DISPUTE_LIMITS.maxLinks) return;
    setLinks((prev) => [...prev, '']);
  };

  const submitResponse = async () => {
    const cleanLinks = links.map((l) => l.trim()).filter(Boolean);
    const error = validateResponseForm({ response, links: cleanLinks });
    if (error) {
      toast.error(error);
      return;
    }
    setSubmitting(true);
    try {
      const updated = await respondDispute(dealId, { response: response.trim(), links: cleanLinks });
      toast.success('Đã gửi giải trình.');
      onResponded?.(updated);
    } catch (e) {
      toast.error(errorMessage(e, 'Gửi giải trình thất bại.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="ui-card flex flex-col gap-3 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="ui-heading text-[15px] font-bold">
          {viewerRole === 'Creator' ? 'Kháng cáo bản quyền' : 'Đang bị kháng cáo'}
        </h2>
        <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_CLS[dispute.status] || ''}`}>
          {disputeStatusLabel(dispute.status)}
        </span>
      </div>

      <div className="text-[13px]">
        <div className="ui-muted">Mô tả kháng cáo</div>
        <p className="ui-text whitespace-pre-wrap">{dispute.description}</p>
      </div>

      {dispute.evidenceLinks.length > 0 && (
        <div className="flex flex-col gap-1 text-[13px]">
          {dispute.evidenceLinks.map((l) => (
            <a key={l} href={l} target="_blank" rel="noreferrer" className="truncate font-semibold text-[#0057FF]">
              {l}
            </a>
          ))}
        </div>
      )}

      {dispute.evidenceImages.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {dispute.evidenceImages.map((img) => (
            <img key={img.id || img.url} src={img.url} alt="" className="size-20 shrink-0 rounded-lg border ui-divider object-cover" />
          ))}
        </div>
      )}

      {dispute.status === 'Open' && !dispute.businessRespondedAt && (
        <p className="text-[13px] font-semibold text-amber-600 dark:text-amber-400">
          Doanh nghiệp còn hạn giải trình: {countdownLabel(dispute.responseDueAt, nowMs)}
        </p>
      )}

      {dispute.businessRespondedAt ? (
        <div className="rounded-lg border ui-divider p-3 text-[13px]">
          <div className="mb-1 font-semibold ui-text">Giải trình của doanh nghiệp</div>
          {dispute.businessResponse && <p className="ui-text whitespace-pre-wrap">{dispute.businessResponse}</p>}
          {(dispute.businessResponseLinks ?? []).map((l) => (
            <a key={l} href={l} target="_blank" rel="noreferrer" className="block truncate font-semibold text-[#0057FF]">
              {l}
            </a>
          ))}
        </div>
      ) : (
        canRespond && (
          <div className="flex flex-col gap-2 rounded-lg border ui-divider p-3">
            <div className="text-[13px] font-semibold ui-text">Giải trình</div>
            <textarea
              value={response}
              onChange={(e) => setResponse(e.target.value)}
              maxLength={BRAND_DISPUTE_LIMITS.responseMaxLength}
              rows={4}
              className="w-full resize-none rounded-md border ui-divider bg-transparent px-3 py-2 text-[14px] outline-none ui-text"
              placeholder="Giải trình vì sao bài nộp bị từ chối đúng quy định…"
            />
            <span className="text-[11px] ui-muted">
              {response.length}/{BRAND_DISPUTE_LIMITS.responseMaxLength}
            </span>
            {links.map((l, idx) => (
              // eslint-disable-next-line react/no-array-index-key
              <input
                key={idx}
                value={l}
                onChange={(e) => updateLink(idx, e.target.value)}
                maxLength={BRAND_DISPUTE_LIMITS.linkMaxLength}
                placeholder="https://... (tuỳ chọn)"
                className="w-full rounded-md border ui-divider bg-transparent px-3 py-2 text-[14px] outline-none ui-text"
              />
            ))}
            {links.length < BRAND_DISPUTE_LIMITS.maxLinks && (
              <button type="button" onClick={addLink} className="self-start text-[13px] font-semibold text-[#0057FF]">
                Thêm link
              </button>
            )}
            <button
              type="button"
              disabled={submitting}
              onClick={submitResponse}
              className="self-start rounded-lg bg-[#0057FF] px-4 py-2 text-[14px] font-semibold text-white disabled:opacity-60"
            >
              {submitting ? 'Đang gửi…' : 'Gửi giải trình'}
            </button>
            <p className="text-[11px] ui-muted">Bạn chỉ được giải trình một lần, hãy kiểm tra kỹ trước khi gửi.</p>
          </div>
        )
      )}

      {dispute.status !== 'Open' && (
        <div className="rounded-lg border ui-divider p-3 text-[13px]">
          <div className="mb-1 font-semibold ui-text">Kết quả</div>
          <p className="ui-text">{disputeStatusLabel(dispute.status)}</p>
          {disputeResultLines(dispute, viewerRole).map((line) => (
            <p key={line} className="font-semibold ui-text">
              {line}
            </p>
          ))}
          {dispute.decisionReason && <p className="ui-muted whitespace-pre-wrap">Lý do: {dispute.decisionReason}</p>}
        </div>
      )}
    </div>
  );
};

export default BrandDisputePanel;
